import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Output, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Grid, GridActionIconClickEvent, GridConfig, GridExportRequestedEvent, GridHyperlinkClickEvent } from '@inavista/components/grid';
import { ServiceOrderService } from 'projects/service/src/app/modules/service-order/services/service-order.service';
import { UserRoles } from '../../models/app.model';
import { CommonService } from '../../services/common.service';
import { exportInavistaGrid } from '../../utils/inavista-grid-export';
import { buildClaimServiceOrdersGridConfig, CLAIM_SERVICE_ORDERS_ACTION_ICONS, CREATE_CLAIM_ACTION } from './service-order-list-for-claim.grid.config';

// Claims page → Add Claim: the caller's service orders a claim can be made for. The arrow starts a claim for the
// order; the order number opens it.
// Removed with the Syncfusion grid (Martin, 2026-10-30): an "Add Cycle Count" button (CycleCountGrid_Toolbar_AddCycleCount)
// that did nothing when clicked, and row checkboxes nothing read.
@Component({
    selector: 'app-service-order-list-for-claim-popup',
    templateUrl: './service-order-list-for-claim-popup.component.html',
    styleUrls: ['./service-order-list-for-claim-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ServiceOrderListForClaimPopupComponent {
  private readonly commonService = inject(CommonService);
  private readonly serviceOrderService = inject(ServiceOrderService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() closeSOlistPopup = new EventEmitter();
  @Output() serviceOrderSelectedForClaim = new EventEmitter<string>();
  @ViewChild('claimServiceOrdersGrid') grid: Grid;

  readonly gridConfig: GridConfig = buildClaimServiceOrdersGridConfig(
    [UserRoles.isc, UserRoles.iscManager, UserRoles.IscSupervisor].includes(this.commonService.USER?.role));
  readonly actionIcons = CLAIM_SERVICE_ORDERS_ACTION_ICONS;
  readonly showLoader = signal(false);
  readonly serviceOrderPopup = signal<{ id: string; data: unknown } | null>(null);

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === CREATE_CLAIM_ACTION) this.serviceOrderSelectedForClaim.emit((event.row as any).serviceOrderId);
  }

  // Same call as before (the ISC order getter, for every role).
  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    const serviceOrderId = (event.row as any).serviceOrderId;
    if (!serviceOrderId) return;
    this.showLoader.set(true);
    this.serviceOrderService.getServiceOrderById(serviceOrderId, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.showLoader.set(false);
        if (res.isSuccessful) this.serviceOrderPopup.set({ id: serviceOrderId, data: res.result });
        else this.commonService.showNotification('error', res.message);
      },
      error: () => {
        this.showLoader.set(false);
        this.commonService.showNotification('error', `Service order ${serviceOrderId} could not be opened.`);
      },
    });
  }

  // Same file as before: the visible columns of the rows loaded, "Service Order List(<date>).xlsx".
  onExportRequested(event: GridExportRequestedEvent) {
    exportInavistaGrid(this.grid, 'Service Order List', event.rows);
  }

  close() {
    this.closeSOlistPopup.emit();
  }
}
